// Generates a "storyboard" (narration script) for a given lesson.
// Matches the structure the user provided. Audio fields are intentionally
// blank — the user generates audio externally and uploads the completed
// JSON back via the Audio Studio page.

import { storyboardStorageKey, presentations } from "./lessons";

/**
 * Build the storyboard entries for the supplied lesson. We emit one entry
 * per fragment for slides that have meaningful caption text, plus one entry
 * for each MCQ / subjective question, plus a final end-of-class wrap-up.
 *
 * Slides are 1-indexed in `slide_no` (matching the sample format).
 */
export const buildStoryboard = (lesson, presentation) => {
    // If the registry has a pre-authored storyboard (e.g. external deck.js
    // lessons whose slide/fragment numbering doesn't match the synthetic
    // photosynthesis-style generator), use it as the base. We will still append
    // the dynamically generated MCQ narrations below.
    const entries = [];
    let order = 0;

    if (presentation?.defaultStoryboard?.length) {
        entries.push(...presentation.defaultStoryboard);
        order = entries.length ? Math.max(...entries.map((e) => e.order || 0)) + 1 : 0;
    } else {
        entries.push({
            type: "say",
            order: order++,
            slide_no: 0,
            fragment_no: 0,
            slug: "lesson-opener",
            text: `Today's lesson: ${lesson.title}. ${lesson.subtitle}.`,
            narration: `Hi class, welcome! Today we're diving into ${lesson.title}. ${lesson.subtitle}.`,
            audio: "",
        });

        lesson.slides.forEach((slide, i) => {
            const slideNo = i + 1;
            const frags = slide.fragments || [];

            if (slide.mode === "mcq_display") {
                entries.push({
                    type: "concept_check",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: 0,
                    slug: slide.id,
                    text: slide.title,
                    question: slide.title,
                    narration: `Quick concept check. ${slide.title}. Take a moment, think it through, and pick the answer that makes the most sense to you.`,
                    audio: "",
                });
                return;
            }

            if (slide.mode === "subjective_display") {
                entries.push({
                    type: "concept_check",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: 0,
                    slug: slide.id,
                    text: slide.title,
                    question: slide.title,
                    narration: `Let's work on a practice problem. ${slide.title}. Read it carefully — we'll walk through it together.`,
                    audio: "",
                });
                return;
            }

            if (slide.mode === "drag_drop") {
                entries.push({
                    type: "say",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: 0,
                    slug: slide.id,
                    text: lesson.activity?.instruction || slide.title,
                    narration: `Now for a quick activity. ${lesson.activity?.instruction || ""} Try dragging each item into the right category. Don't worry about getting it perfect — we'll review it together.`,
                    audio: "",
                });
                return;
            }

            if (slide.mode === "video_teaching") {
                entries.push({
                    type: "say",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: 0,
                    slug: slide.id,
                    text: slide.title,
                    narration: `Let's watch a short clip. As you watch, look out for the key details — we'll discuss them right after.`,
                    audio: "",
                });
                return;
            }

            if (slide.mode === "real_life_example" || slide.mode === "blank_slide") {
                return;
            }

            if (frags.length === 0) {
                entries.push({
                    type: "say",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: 0,
                    slug: slide.id,
                    text: slide.title,
                    narration: slide.title,
                    audio: "",
                });
                return;
            }

            frags.forEach((frag, fIdx) => {
                const text = frag.bullet || frag.caption || slide.title;
                const narration = frag.caption || frag.bullet || slide.title;
                entries.push({
                    type: "say",
                    order: order++,
                    slide_no: slideNo,
                    fragment_no: fIdx,
                    slug: slide.id,
                    text,
                    narration,
                    audio: "",
                });
            });
        });

        entries.push({
            type: "end_class",
            order: order++,
            slide_no: lesson.slides.length,
            fragment_no: 0,
            narration: `Great work today, class! That wraps up our lesson on ${lesson.title}.`,
            audio: "",
        });
    }

    // MCQ question and explanation narrations
    (lesson.mcqs || []).forEach((mcq) => {
        if (!entries.some((e) => e.mcq_id === mcq.id && e.type === "mcq")) {
            entries.push({
                type: "mcq",
                order: order++,
                mcq_id: mcq.id,
                slug: mcq.id,
                text: mcq.question || mcq.q,
                narration: mcq.question || mcq.q,
                audio: "",
            });
        }
        if (!entries.some((e) => e.mcq_id === mcq.id && e.type === "mcq_explanation")) {
            const explanationText = mcq.explain || mcq.explanation || `The correct answer is Option ${mcq.correct_option_id || ""}.`;
            entries.push({
                type: "mcq_explanation",
                order: order++,
                mcq_id: mcq.id,
                slug: `${mcq.id}_explanation`,
                text: explanationText,
                narration: explanationText,
                audio: "",
            });
        }
    });

    // Real-life example popup narrations — played when the teacher opens
    // an example as a bonus popup on the student display.
    (lesson.realLifeExamples || []).forEach((ex) => {
        if (!entries.some((e) => e.example_id === ex.id && e.type === "example")) {
            entries.push({
                type: "example",
                order: order++,
                example_id: ex.id,
                slug: ex.id,
                text: ex.headline || ex.text,
                narration:
                    ex.description ||
                    ex.text ||
                    `Here's a real-life example: ${ex.headline}.`,
                audio: "",
            });
        }
    });

    return entries;
};

/**
 * Find the storyboard entry for a real-life example popup.
 */
export const findExampleStoryboardEntry = (storyboard, exampleId) => {
    if (!storyboard?.length || !exampleId) return null;
    return (
        storyboard.find(
            (e) =>
                e.type === "example" &&
                (e.example_id === exampleId || e.slug === exampleId) &&
                e.audio,
        ) || null
    );
};

/**
 * Find the storyboard entry for a specific MCQ question.
 */
export const findMcqStoryboardEntry = (storyboard, mcqId) => {
    if (!storyboard?.length || !mcqId) return null;
    return (
        storyboard.find(
            (e) =>
                e.type === "mcq" &&
                (e.mcq_id === mcqId || e.slug === mcqId) &&
                e.audio,
        ) || null
    );
};

/**
 * Find the storyboard entry for a specific MCQ explanation/reveal.
 */
export const findMcqExplanationStoryboardEntry = (storyboard, mcqId) => {
    if (!storyboard?.length || !mcqId) return null;
    return (
        storyboard.find(
            (e) =>
                e.type === "mcq_explanation" &&
                (e.mcq_id === mcqId || e.slug === `${mcqId}_explanation`) &&
                e.audio,
        ) || null
    );
};

/**
 * Find the storyboard entry that matches the current (slideIndex, fragmentIndex).
 * `slideIndex` is 0-based; storyboard uses 1-based `slide_no`.
 */
export const findStoryboardEntry = (storyboard, slideIndex, fragmentIndex) => {
    if (!storyboard?.length) return null;
    const slideNo = slideIndex + 1;
    return (
        storyboard.find(
            (e) =>
                e.slide_no === slideNo &&
                (e.fragment_no ?? 0) === (fragmentIndex ?? 0) &&
                (e.type === "say" || e.type === "concept_check") &&
                e.audio,
        ) || null
    );
};

export const loadStoredStoryboard = (presentationId = "photosynthesis") => {
    try {
        const raw = localStorage.getItem(storyboardStorageKey(presentationId));
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch {
        /* noop */
    }
    const p = presentations.find((x) => x.id === presentationId);
    return p?.defaultStoryboard || null;
};

export const saveStoredStoryboard = (presentationId, entries) => {
    try {
        localStorage.setItem(
            storyboardStorageKey(presentationId),
            JSON.stringify(entries),
        );
        return true;
    } catch {
        return false;
    }
};

export const clearStoredStoryboard = (presentationId) => {
    try {
        localStorage.removeItem(storyboardStorageKey(presentationId));
    } catch {
        /* noop */
    }
};

// Convenience: present a list of all lessons + their audio-loaded status.
export const allPresentationsWithStatus = () =>
    presentations.map((p) => {
        const stored = loadStoredStoryboard(p.id);
        return {
            ...p,
            hasAudio: !!stored,
            audioCount: (stored || []).filter((e) => e.audio).length,
            totalCount: stored?.length || 0,
        };
    });
